import { useEffect, useState } from "react";
import { Sparkle, TrendDown, ChartDonut, Lightning, Buildings } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell, PieChart, Pie } from "recharts";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { formatINR } from "../../lib/format";
import { Input } from "../../components/ui/input";

const thisMonth = () => new Date().toISOString().slice(0,7);
const CLASS_COLOR = { variable: "#D90429", fixed: "#0A0A0A" };

export default function FinanceAnalysis() {
  const { active, activeId } = useCompany();
  const navigate = useNavigate();
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/finance/analysis", { params: { company_id: activeId, month } });
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { run(); }, [activeId, month]);

  if (!activeId) {
    return <Layout title="AI Analysis"><div className="border border-black/10 p-16 text-center"><Buildings size={36} className="mx-auto text-[#D90429]"/><h3 className="font-display text-2xl font-bold tracking-tight mt-4">Pick a company first</h3><button onClick={()=>navigate("/finance/companies")} className="mt-5 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">Go to Companies</button></div></Layout>;
  }

  const drains = data?.drains || [];
  const totals = data?.totals || {};
  const topDrains = drains.slice(0, 8);
  const variableSum = drains.filter(d=>d.classification==="variable").reduce((s,d)=>s+d.amount_inr,0);
  const fixedSum = drains.filter(d=>d.classification==="fixed").reduce((s,d)=>s+d.amount_inr,0);
  const splitData = [
    { name: "Variable (production-proportional)", value: variableSum },
    { name: "Fixed / Overhead", value: fixedSum },
  ];

  return (
    <Layout
      title="AI Money-Drain Analysis"
      subtitle={`${active?.name} · ${month}`}
      actions={
        <>
          <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="w-44 rounded-none" data-testid="analysis-month-picker"/>
          <button onClick={run} disabled={loading} data-testid="rerun-analysis" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#D90429] active:scale-95 disabled:opacity-50">
            <Sparkle size={16} weight="fill"/> {loading ? "Analysing…" : "Re-run AI Analysis"}
          </button>
        </>
      }
    >
      {/* Totals */}
      <div className="grid grid-cols-1 md:grid-cols-5 border border-black/10 bg-white mb-6" data-testid="analysis-totals">
        {[
          {label:"Direct (WO)", value: totals.direct_expenses_inr, color:"#D90429", testId:"total-direct"},
          {label:"Operating", value: totals.operating_expenses_inr, color:"#F4A261", testId:"total-operating"},
          {label:"Payroll", value: totals.payroll_inr, color:"#3A86FF", testId:"total-payroll"},
          {label:"Incentives Paid", value: totals.incentives_paid_inr, color:"#2A9D8F", testId:"total-incentives"},
          {label:"Grand Total Burn", value: totals.grand_total_inr, color:"#0A0A0A", testId:"total-burn"},
        ].map((s)=>(
          <div key={s.label} data-testid={s.testId} className="p-5 border-r border-black/5 last:border-r-0">
            <div className="overline" style={{color:s.color}}>{s.label}</div>
            <div className="font-display text-3xl font-black tracking-tighter mt-2" style={{color:s.color}}>{formatINR(s.value)}</div>
          </div>
        ))}
      </div>

      {/* AI Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
        <div className="lg:col-span-7 border border-black/10 bg-[#0A0A0A] text-white p-6" data-testid="ai-insights-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkle size={18} weight="fill" className="text-[#D90429]"/>
            <span className="font-mono-shelv text-[10px] uppercase tracking-[0.2em] text-white/60">Gemini · CFO commentary</span>
          </div>
          <h3 className="font-display text-2xl font-bold tracking-tight mb-3">Where the money is draining</h3>
          <p className="text-white/85 leading-relaxed" data-testid="ai-insights-text">{data?.insights || (loading ? "Analysing…" : "—")}</p>
        </div>

        <div className="lg:col-span-5 border border-black/10 bg-white p-6">
          <div className="overline">Variable vs Fixed</div>
          <h3 className="font-display text-2xl font-bold tracking-tight mb-3">Which costs scale with production?</h3>
          <div style={{ width:"100%", height: 200 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={splitData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80} paddingAngle={2}>
                  <Cell fill="#D90429"/><Cell fill="#0A0A0A"/>
                </Pie>
                <Tooltip contentStyle={{border:"1px solid #0A0A0A", borderRadius:0}} formatter={(v)=>formatINR(v)}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
            <div className="border-l-2 border-[#D90429] pl-3"><div className="font-mono-shelv uppercase tracking-widest text-[10px] text-[#D90429]">Variable</div><div className="font-display text-lg font-bold">{formatINR(variableSum)}</div></div>
            <div className="border-l-2 border-[#0A0A0A] pl-3"><div className="font-mono-shelv uppercase tracking-widest text-[10px]">Fixed</div><div className="font-display text-lg font-bold">{formatINR(fixedSum)}</div></div>
          </div>
        </div>
      </div>

      {/* Drain ranking */}
      <div className="border border-black/10 bg-white p-6 mb-6">
        <div className="overline">Top drains</div>
        <h3 className="font-display text-2xl font-bold tracking-tight mb-4">Highest-cost lines this month</h3>
        {topDrains.length === 0 ? <p className="text-sm text-[#717680]">No expenses logged for {month}.</p> : (
        <div style={{ width:"100%", height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={topDrains} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid stroke="#EBECEF" horizontal={false}/>
              <XAxis type="number" stroke="#717680" fontSize={11} tickFormatter={(v)=>`₹${(v/1000).toFixed(0)}k`}/>
              <YAxis type="category" dataKey="label" stroke="#717680" fontSize={11} width={160}/>
              <Tooltip cursor={{fill:"#F5F6F8"}} contentStyle={{border:"1px solid #0A0A0A", borderRadius:0}} formatter={(v)=>formatINR(v)}/>
              <Bar dataKey="amount_inr" data-testid="drain-bar">
                {topDrains.map((d, i)=>(<Cell key={`drain-cell-${d.label || i}`} fill={CLASS_COLOR[d.classification] || "#717680"}/>))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        )}
        <div className="flex items-center gap-4 mt-3 text-xs">
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[#D90429]"/>Variable / production-proportional</div>
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[#0A0A0A]"/>Fixed / overhead</div>
        </div>
      </div>

      {/* Drain table */}
      <div className="border border-black/10 bg-white overflow-x-auto mb-6">
        <table className="w-full text-sm min-w-[640px]" data-testid="drains-table">
          <thead className="bg-[#F5F6F8] text-left"><tr>{["Line","Type","Classification","Amount"].map(h=>(<th key={h} className="px-4 py-3 font-mono-shelv text-xs uppercase tracking-widest text-[#717680]">{h}</th>))}</tr></thead>
          <tbody>
            {drains.length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-[#717680]">No spend data yet.</td></tr>}
            {drains.map((d, i)=>(
              <tr key={`drain-${d.label || i}`} className="border-t border-black/5 hover:bg-[#FAFBFC]">
                <td className="px-4 py-2 font-semibold">{d.label}</td>
                <td className="px-4 py-2 text-xs font-mono-shelv uppercase tracking-widest text-[#717680]">{d.type}</td>
                <td className="px-4 py-2"><span className="text-xs font-mono-shelv uppercase tracking-widest px-2 py-0.5" style={{background:(CLASS_COLOR[d.classification]||"#0A0A0A")+"15", color: CLASS_COLOR[d.classification]||"#0A0A0A"}}>{d.classification}</span></td>
                <td className="px-4 py-2 font-mono-shelv font-bold">{formatINR(d.amount_inr)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Incentive recs */}
      <div className="border border-black/10 bg-white p-6" data-testid="incentive-recs-card">
        <div className="flex items-center gap-2 mb-3">
          <Lightning size={18} weight="bold" className="text-[#D90429]"/>
          <span className="font-mono-shelv text-[10px] uppercase tracking-[0.2em] text-[#D90429]">Incentive ideas</span>
        </div>
        <h3 className="font-display text-2xl font-bold tracking-tight mb-4">Spend that moves the needle on production</h3>
        {data?.top_operators?.length > 0 && (
          <div className="mb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {data.top_operators.map((o, i) => (
              <div key={`top-op-${o.operator || i}`} className="border border-black/10 p-3"><div className="overline text-[10px]">#{i+1} operator</div><div className="font-display text-lg font-bold tracking-tight">{o.operator}</div><div className="text-xs text-[#717680] font-mono-shelv">{o.qc_passed_pages.toLocaleString("en-IN")} QC pages</div></div>
            ))}
          </div>
        )}
        {data?.incentive_recommendations?.length ? (
          <ul className="space-y-2" data-testid="incentive-list">
            {data.incentive_recommendations.map((r, i)=>(
              <li key={`rec-${i}-${String(r).slice(0,16)}`} className="flex items-start gap-3 border-l-2 border-[#D90429] pl-4 py-1 text-sm leading-relaxed">{r}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-[#717680]">No recommendations available yet. Log some productivity and re-run.</p>
        )}
      </div>
    </Layout>
  );
}
